import CheckIcon from "@mui/icons-material/Check";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { CONTROL_HEIGHT } from "../../theme/tokens";
import { agencySourceLabel } from "../../utils/agencyIntegrations";
import { getAgencySubRoleInfo } from "../../utils/agencySubRoles";
import type { AgencySelectionDraft } from "../../utils/agencyTargets";
import type { AgencySource, NurseRole } from "../../utils/models";
import { InHouseTooltip } from "../InHouseTooltip/InHouseTooltip";
import { useGetDepartmentSubRoles } from "../staffingSearch/useGetDepartmentSubRoles";
import { SystemRoundedCheckbox } from "../UIToolkit/Clickable/SystemRoundedCheckbox";
import { SystemChips } from "../UIToolkit/Clickable/TabsAndChips/SystemChips/SystemChips";
import { SystemDropdown } from "../UIToolkit/Inputs/SystemDropDown";
import { SystemBanner } from "../UIToolkit/molecules/SystemBanner";
import { AgencyTimingFields } from "./AgencyTimingFields";

export function AgencySelectionFields({
	agencies,
	value,
	onChange,
	jobId,
	roleGroups,
	jobManagementEnabled,
	dataTestId,
	mode,
	compact,
}: {
	agencies: AgencySource[];
	value: AgencySelectionDraft[];
	onChange: (value: AgencySelectionDraft[]) => void;
	jobId?: string;
	roleGroups: string[];
	jobManagementEnabled: boolean;
	dataTestId: string;
	mode: "create" | "add";
	/** Inline controls for a shift card; the surrounding panel owns the shared explanation. */
	compact?: boolean;
}) {
	const { t } = useTranslation();
	const { chosenDepartment } = useHospitalData();
	const catalog = useGetDepartmentSubRoles(chosenDepartment.department_id);
	if (catalog.isPending) return <Typography role="status">{t("loading")}</Typography>;
	if (catalog.isError) return <SystemBanner tone="error" title={t("agency.multi.roles.error")} />;
	if (!catalog.data) return null; // Query has no successful result yet; never infer a role.
	const agencyOptions = agencies.map((source) => ({
		source,
		options: getAgencySubRoleInfo({
			catalog: catalog.data,
			agencySource: source,
			jobManagementEnabled,
			roleGroups: chosenDepartment.role_groups,
			row: { jobId, roleGroups },
		}).options,
		draft: value.find((selection) => selection.agency_source === source),
	}));
	const toggleAgency = (source: AgencySource, checked: boolean): void => {
		// DEFAULT: newly selected agencies visibly start at "Post now"; a role must be chosen explicitly.
		onChange(
			checked
				? [...value, { agency_source: source, role: undefined, timing: "now", hours: "" }]
				: value.filter((selection) => selection.agency_source !== source),
		);
	};
	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: compact ? 1 : 2, minWidth: 0 }} data-testid={dataTestId}>
			{!compact && (
				<Typography variant="body2" color="text.secondary">
					{t(mode === "create" ? "agency.multi.selection.hint" : "agency.multi.add.hint")}
				</Typography>
			)}
			{compact && (
				<Box role="group" aria-label={t("agency.multi.details")} data-testid={`${dataTestId}-chips`} sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
					{agencyOptions.map(({ source, options, draft }) => {
						const item = {
							value: source,
							label: agencySourceLabel(source),
							disabled: options.length === 0 && draft === undefined,
							ariaLabel: options.length === 0 ? `${agencySourceLabel(source)}: ${t("agency.multi.roles.unavailable")}` : agencySourceLabel(source),
							endAdornment: draft ? <CheckIcon fontSize="inherit" /> : undefined,
						};
						return (
							<InHouseTooltip key={source} title={options.length === 0 ? t("agency.multi.roles.unavailable") : ""}>
								<Box component="span" sx={{ display: "inline-flex" }}>
									<SystemChips
										items={[item]}
										selectedItems={draft ? [item] : []}
										onChange={(selected, checked) => toggleAgency(selected.value, checked)}
										variant="filled"
										announcePressed
										touchTarget
										forceFitContent
										getItemDataTestId={(chip) => `${dataTestId}-${chip.value}-selected`}
									/>
								</Box>
							</InHouseTooltip>
						);
					})}
				</Box>
			)}
			{agencyOptions.map(({ source, options, draft }) => {
				if (compact && !draft) return null;
				const update = (next: AgencySelectionDraft) => onChange(value.map((selection) => (selection.agency_source === source ? next : selection)));
				return (
					<Box
						key={source}
						sx={{
							p: compact ? 1 : 2,
							border: 1,
							borderColor: draft ? "primary.main" : "divider",
							borderRadius: 1,
							display: "grid",
							gridTemplateColumns: compact ? { xs: "minmax(0, 1fr)", md: "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 2fr)" } : "minmax(0, 1fr)",
							alignItems: "start",
							gap: 1.5,
						}}
					>
						{compact ? (
							<Typography variant="body2" sx={{ minHeight: CONTROL_HEIGHT.STANDARD, display: "flex", alignItems: "center" }}>
								{agencySourceLabel(source)}
							</Typography>
						) : (
							<SystemRoundedCheckbox
								checked={draft !== undefined}
								label={agencySourceLabel(source)}
								disabled={options.length === 0 && draft === undefined}
								tooltip={options.length === 0 ? t("agency.multi.roles.unavailable") : undefined}
								data-testid={`${dataTestId}-${source}-selected`}
								onChange={(checked) => toggleAgency(source, checked)}
							/>
						)}
						{draft && (
							<>
								<SystemDropdown<NurseRole>
									label={t(compact ? "role" : "sub.role")}
									selectedItem={draft.role}
									items={options.map((role) => ({ value: role, label: role }))}
									onChange={(role) => update({ ...draft, role })}
									fullWidth
									data-testid={`${dataTestId}-${source}-role`}
								/>
								<AgencyTimingFields
									timing={draft.timing}
									hours={draft.hours}
									onChange={(timing, hours) => update({ ...draft, timing, hours })}
									dataTestId={`${dataTestId}-${source}`}
								/>
							</>
						)}
					</Box>
				);
			})}
		</Box>
	);
}
